import React from 'react';

import {Select} from '@famly/mf_input_select';
import {FormControl} from '@famly/mf_input_form-control';
import {Button} from '@famly/mf_input_button';
import {Stack} from '@famly/mf_layout_stack';
import {Box} from '@famly/mf_layout_box';
import {needsTranslations} from 'web-app/util/scoped-needs-translation';
import {hasValue} from '@famly/stat_ts-utils_has-value';

import type * as Types from '../types';
import {AppendField} from './append-field';
import {OptionAdornment} from './option-adornment';

type FeesProps = {
    onChange: Types.HandleFeesChange;
    appendField: {onClick: Types.AppendFeeField; isDisabled: boolean};
    removeField: Types.RemoveFeeField;
    fields: Types.State['feesState']['inputFields'];
    feeOptions: Array<Types.TuitionAndFeeOption>;
    toggleField: Types.ToggleFeeField;
    fieldCurrentlyOpen: null | number;
    isDisabled?: boolean;
};

export const Fees = ({
    onChange,
    appendField,
    removeField,
    fields,
    feeOptions,
    toggleField,
    fieldCurrentlyOpen,
    isDisabled,
}: FeesProps) => {
    return (
        <FormControl label={needsTranslations('SimplifiedUsBilling', 'Additional lines')}>
            <Stack direction="column" spacing={2}>
                {fields.map((field, index) => {
                    return (
                        <Box key={index}>
                            <Stack alignItems="center">
                                <Select
                                    fullWidth
                                    onChange={selectedOption => onChange(index, selectedOption)}
                                    options={feeOptions}
                                    getOptionAdornment={option => {
                                        if (option.type === 'add-new-item') {
                                            return null;
                                        }
                                        return (
                                            <OptionAdornment>
                                                $
                                                {hasValue(option.lowestAndHighestPrice.lowest)
                                                    ? `${option.lowestAndHighestPrice.lowest?.toFixed(2)} - `
                                                    : null}
                                                {option.lowestAndHighestPrice.highest.toFixed(2)}
                                            </OptionAdornment>
                                        );
                                    }}
                                    value={field.id}
                                    open={fieldCurrentlyOpen === index}
                                    onClose={() => toggleField(null)}
                                    onOpen={() => toggleField(index)}
                                    data-e2e-class="fee-select"
                                    disabled={isDisabled}
                                />
                                <Button
                                    data-e2e-class="remove-fee-button"
                                    variant="transparent"
                                    icon="delete"
                                    aria-label={needsTranslations('SimplifiedUsBilling', 'Delete added fee')}
                                    onClick={() => {
                                        removeField(index);
                                    }}
                                />
                            </Stack>
                        </Box>
                    );
                })}
                <Box mt={2} alignSelf="start" data-e2e-id="add-fee-button">
                    <AppendField
                        text={needsTranslations('SimplifiedUsBilling', 'Add a fee')}
                        appendField={() => appendField.onClick(fields.length)}
                        disabled={appendField.isDisabled}
                    />
                </Box>
            </Stack>
        </FormControl>
    );
};
